import React, { useEffect, useRef, useState } from "react";
import { Alert, Avatar, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { settingsRequest } from "../../utils/settingsApi";
import { FieldsGrid, SettingsActions, SettingsCard, SettingsField, SettingsTable, SettingsToggle } from "./SettingsPrimitives";
import { timezones } from "./SettingsGeneral";
import { readStoredUser, writeStoredUser } from "../../utils/userSession";

export default function SettingsPersonal({ tab, user, values, onSaved }) {
  const [profile, setProfile] = useState(null); const [draft, setDraft] = useState({}); const [busy, setBusy] = useState(false); const [feedback, setFeedback] = useState(null);
  useEffect(() => { let active = true;
    settingsRequest(`profile/${user.id}`).then(data => { if (active) { const [first_name = "", ...rest] = String(data.name || "").split(" "); const next = { ...data, first_name, last_name: rest.join(" ") }; setProfile(next); setDraft(next); } }).catch(err => { if (active) setFeedback({ severity: "error", text: err.message }); });
    return () => { active = false; };
  }, [user.id]);
  const change = (key, value) => setDraft(current => ({ ...current, [key]: value }));
  const save = async () => {
    setBusy(true); setFeedback(null);
    try {
      const name = [draft.first_name, draft.last_name].map(value => String(value || "").trim()).filter(Boolean).join(" ");
      await settingsRequest(`profile/${user.id}`, { method: "PUT", body: { ...draft, name } });
      const saved = await settingsRequest("settings/workspace/personal", { method: "PUT", body: { phone: draft.phone || values?.phone || "", language: draft.language || values?.language || "English" } });
      setProfile(draft); onSaved(saved.values);
      writeStoredUser({ ...readStoredUser(), name });
      setFeedback({ severity: "success", text: "Profile saved." });
    } catch (err) { setFeedback({ severity: "error", text: err.message }); } finally { setBusy(false); }
  };
  const uploadPhoto = async event => {
    const file = event.target.files?.[0]; event.target.value = ""; if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 1000000) { setFeedback({ severity: "error", text: "Choose a PNG, JPEG, or WebP photo smaller than 1 MB." }); return; }
    setBusy(true); setFeedback(null);
    try { const body = new FormData(); body.append("profile_image", file); const data = await settingsRequest("update-profile-image", { method: "POST", body });
      setProfile(current => ({ ...current, profile_image: data.profile_image })); setDraft(current => ({ ...current, profile_image: data.profile_image })); writeStoredUser({ ...readStoredUser(), profile_image: data.profile_image });
      setFeedback({ severity: "success", text: "Profile photo updated." });
    } catch (err) { setFeedback({ severity: "error", text: err.message }); } finally { setBusy(false); }
  };
  if (tab === 2) return <PersonalNotifications values={values} onSaved={onSaved} />;
  if (tab === 1) return <PersonalEmail values={values} onSaved={onSaved} />;
  const dirty = JSON.stringify(draft) !== JSON.stringify(profile || {});
  return <SettingsCard title="Profile" description="Manage your personal information and account details.">
    {feedback && <Alert severity={feedback.severity} sx={{ mb: 3 }}>{feedback.text}</Alert>}
    {!profile ? <CircularProgress size={24} aria-label="Loading profile" /> : <>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}><Avatar src={profile.profile_image} sx={{ width: 42, height: 42 }}>{draft.first_name?.[0]}</Avatar><Button component="label" disabled={busy}>Change Photo<input type="file" hidden accept="image/png,image/jpeg,image/webp" onChange={uploadPhoto} /></Button></Stack>
      <FieldsGrid><SettingsField id="settings-profile-first-name" label="First name" value={draft.first_name} disabled={busy} onChange={value => change("first_name", value)} /><SettingsField id="settings-profile-last-name" label="Last name" value={draft.last_name} disabled={busy} onChange={value => change("last_name", value)} /><SettingsField id="settings-profile-title" label="Professional Title" value={draft.title} disabled={busy} onChange={value => change("title", value)} /><SettingsField id="settings-profile-phone" label="Phone" value={draft.phone ?? values?.phone ?? ""} disabled={busy || !values} onChange={value => change("phone", value)} /><SettingsField id="settings-profile-timezone" label="Timezone" options={timezones.includes(draft.timezone) ? timezones : [...timezones, ...(draft.timezone ? [draft.timezone] : [])]} value={draft.timezone} disabled={busy} onChange={value => change("timezone", value)} /><SettingsField id="settings-profile-language" label="Language" options={["English"]} value={draft.language || values?.language || "English"} disabled={busy || !values} onChange={value => change("language", value)} /></FieldsGrid>
      <SettingsActions dirty={dirty && !!draft.first_name?.trim() && !!values} busy={busy} onCancel={() => { setDraft(profile); setFeedback(null); }} onSave={save} />
    </>}
  </SettingsCard>;
}

const personalNotificationTypes = [
  ["assignments_mentions", "Assignments and mentions"],
  ["candidate_updates", "Candidate / recruiting updates"],
  ["sales_follow_ups", "Sales / CRM follow-ups"],
  ["conversations", "Conversations"],
  ["calendar_changes", "Calendar / interview changes"],
  ["agent_review_required", "Agent review required", true],
  ["connector_critical_failure", "Connector critical failure", true],
  ["account_security_event", "Account / security event", true],
  ["permission_change", "Permission change affecting the user", true],
  ["user_action_failure", "Failure of a user-initiated action", true],
];

function PersonalNotifications({ values, onSaved }) {
  const [draft, setDraft] = useState(values || {});
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const dirtyRef = useRef(false);
  useEffect(() => { if (!dirtyRef.current) setDraft(values || {}); }, [values]);
  const update = (key, channel, checked) => {
    dirtyRef.current = true;
    setDraft(current => ({
      ...current,
      notification_preferences: {
        ...current.notification_preferences,
        [key]: { ...current.notification_preferences?.[key], [channel]: checked },
      },
    }));
  };
  const save = async () => {
    setBusy(true); setFeedback(null);
    try {
      const result = await settingsRequest("settings/workspace/personal", { method: "PUT", body: {
        notification_overrides: draft.notification_overrides !== false,
        notification_preferences: draft.notification_preferences || {},
      } });
      setDraft(current => ({ ...current, ...result.values }));
      dirtyRef.current = false;
      onSaved?.(result.values);
      setFeedback({ severity: "success", text: "Notification preferences saved." });
    } catch (error) { setFeedback({ severity: "error", text: error.message }); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    const notification_preferences = Object.fromEntries(personalNotificationTypes.map(([key]) => [key, { in_app: true, email: true }]));
    setBusy(true); setFeedback(null);
    try {
      const result = await settingsRequest("settings/workspace/personal", { method: "PUT", body: { notification_overrides: false, notification_preferences } });
      setDraft(current => ({ ...current, ...result.values }));
      dirtyRef.current = false;
      onSaved?.(result.values);
      setFeedback({ severity: "success", text: "Organization notification defaults will now be used." });
    } catch (error) { setFeedback({ severity: "error", text: error.message }); }
    finally { setBusy(false); }
  };
  return <SettingsCard title="Personal Notifications" description="Personal overrides are preserved while turned off and restored when turned back on.">
    {feedback && <Alert severity={feedback.severity} sx={{ mb: 3 }}>{feedback.text}</Alert>}
    <SettingsToggle label="Personal notification overrides" value={draft.notification_overrides !== false} disabled={busy} onChange={value => { dirtyRef.current = true; setDraft(current => ({ ...current, notification_overrides: value })); }} />
    <SettingsTable headers={["Notification Type", "In-App", "Email", "Required"]} rows={personalNotificationTypes.map(([key, label, required]) => {
      const preference = draft.notification_preferences?.[key] || {};
      return [label,
        <SettingsToggle label={`${label} in-app`} value={preference.in_app ?? true} disabled={busy || draft.notification_overrides === false || required} onChange={value => update(key, "in_app", value)} />,
        <SettingsToggle label={`${label} email`} value={preference.email ?? true} disabled={busy || draft.notification_overrides === false || required} onChange={value => update(key, "email", value)} />,
        required ? "Yes" : "No"];
    })} />
    <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
      <Button variant="outlined" disabled={busy} onClick={reset}>Reset to Organization Defaults</Button>
      <Button variant="contained" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save Preferences"}</Button>
    </Stack>
  </SettingsCard>;
}

function PersonalEmail({ values, onSaved }) {
  const [draft, setDraft] = useState(values || {});
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const dirtyRef = useRef(false);
  useEffect(() => { if (!dirtyRef.current) setDraft(values || {}); }, [values]);
  const save = async () => {
    setBusy(true); setFeedback(null);
    try {
      const result = await settingsRequest("settings/workspace/personal", { method: "PUT", body: {
        signature: draft.signature || "",
        use_organization_signature: !!draft.use_organization_signature,
        disable_signature: !!draft.disable_signature,
      } });
      setDraft(current => ({ ...current, ...result.values }));
      dirtyRef.current = false;
      onSaved?.(result.values);
      setFeedback({ severity: "success", text: "Email settings saved." });
    } catch (error) { setFeedback({ severity: "error", text: error.message }); }
    finally { setBusy(false); }
  };
  return <SettingsCard title="Email Settings" description="Personal signatures take precedence over the organization default.">
    {feedback && <Alert severity={feedback.severity} sx={{ mb: 3 }}>{feedback.text}</Alert>}
    <Stack spacing={2}>
      <SettingsToggle label="Use Organization Default" value={!!draft.use_organization_signature} disabled={busy} onChange={value => { dirtyRef.current = true; setDraft(current => ({ ...current, use_organization_signature: value })); }} />
      <SettingsField label="Personal Email Signature" value={draft.signature || ""} multiline disabled={busy || draft.use_organization_signature} onChange={value => { dirtyRef.current = true; setDraft(current => ({ ...current, signature: value })); }} />
      <SettingsToggle label="Disable Signature" value={!!draft.disable_signature} disabled={busy || draft.use_organization_signature} onChange={value => { dirtyRef.current = true; setDraft(current => ({ ...current, disable_signature: value })); }} />
      <Typography variant="subtitle2">Preview</Typography>
      <Typography sx={{ whiteSpace: "pre-wrap", minHeight: 40 }}>{draft.disable_signature ? "No signature" : draft.use_organization_signature ? "Organization default signature" : draft.signature || "No signature set."}</Typography>
    </Stack>
    <Button variant="contained" sx={{ mt: 3 }} disabled={busy} onClick={save}>{busy ? "Saving…" : "Save Email Settings"}</Button>
  </SettingsCard>;
}
